import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { BASE_URL, DEFAULT_OG_IMAGE, OG_LOCALE, OG_SITE_NAME, getSeoForPath } from "../config/seoConfig";

const normalizePath = (value) => {
  const path = String(value || "/").trim();
  if (!path) return "/";
  if (path === "/") return "/";
  const withLeadingSlash = path.startsWith("/") ? path : `/${path}`;
  const hasTrailingSlash = withLeadingSlash.endsWith("/");
  const withoutTrailingSlash = withLeadingSlash.replace(/\/+$/, "");
  if (!withoutTrailingSlash) return "/";
  return hasTrailingSlash ? `${withoutTrailingSlash}/` : withoutTrailingSlash;
};

const resolveUrl = (value) => {
  if (!value) return BASE_URL;
  try {
    return new URL(value, BASE_URL).toString();
  } catch {
    return BASE_URL;
  }
};

const SEO = () => {
  const { pathname } = useLocation();
  const seo = getSeoForPath(pathname);
  const canonicalPath = normalizePath(seo.canonicalPath || pathname || "/");
  const canonicalUrl = resolveUrl(canonicalPath);
  const title = seo.title || "ZediPass";
  const description = seo.description || "";
  const ogImage = resolveUrl(seo.ogImage || DEFAULT_OG_IMAGE);
  const ogImageAlt = seo.ogImageAlt || "ZediPass loyalty program software preview";
  const robots = seo.robots || "index,follow";
  const ogType = seo.ogType || "website";
  const schemaBlocks = Array.isArray(seo.schema) ? seo.schema : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={OG_SITE_NAME} />
      <meta property="og:locale" content={OG_LOCALE} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={ogImageAlt} />
      {seo.ogUpdatedTime ? <meta property="og:updated_time" content={seo.ogUpdatedTime} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      {seo.twitterSite ? <meta name="twitter:site" content={seo.twitterSite} /> : null}

      {schemaBlocks.map((schema, index) => (
        <script
          key={`schema-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;
